import { Injectable, inject } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import { Platform } from '@ionic/angular';
import type * as fs_typeonly from 'fs';
import { cloneDeep, isEqual } from 'lodash';
import type * as path_typeonly from 'path';
import { BehaviorSubject, Observable, firstValueFrom, map } from 'rxjs';
import type * as tmp_typeonly from 'tmp';
import { WaitPromiser } from './core';
import { electronBridge } from './electron-bridge';
import { LOGGER, Logger } from './logger';

declare function __non_webpack_require__(p: string): any;
const nodeRequire =
    typeof __non_webpack_require__ !== 'undefined' ? __non_webpack_require__ : typeof require !== 'undefined' ? require : undefined;

let fs: typeof fs_typeonly;
let path: typeof path_typeonly;
let tmp: typeof tmp_typeonly;

if (nodeRequire) {
    try {
        fs = nodeRequire('fs');
    } catch {}
    try {
        path = nodeRequire('path');
    } catch {}
    try {
        tmp = nodeRequire('tmp');
    } catch {}
}

// for lightweight storage only, wraps Preferences on Capacitor and JSON config on Electron
@Injectable({ providedIn: 'root' })
export class PersistentStore {
    private readonly logger = inject<Logger>(LOGGER);
    private electronDirPath: string;
    private electronFilePath: string;
    private subjects = new Map<string, BehaviorSubject<any>>();
    private readyPromiser = new WaitPromiser();

    constructor() {
        const platform = inject(Platform);
        if (electronBridge && fs && path) {
            this.electronDirPath = electronBridge.statics.app.path.userData;
            fs.mkdirSync(this.electronDirPath, { recursive: true });
            this.electronFilePath = path.join(this.electronDirPath, 'settings.json');

            // create an empty file if none exists, otherwise don't touch it
            try {
                fs.writeFileSync(this.electronFilePath, '{}', { flag: 'wx' });
            } catch {}

            // Watch the directory rather than the file: atomic saves (write-temp + rename)
            // replace the file's inode, causing file-based watchers to stop after the first rename.
            const settingsFileName = path.basename(this.electronFilePath);
            fs.watch(this.electronDirPath, { persistent: false }, (_event, filename) => {
                if (filename === settingsFileName) {
                    this.electronLoadFile();
                }
            });

            this.electronLoadFile();
            this.readyPromiser.notify();
        } else {
            platform.ready().then(async () => {
                await Preferences.configure({});
                const keys = (await Preferences.keys()).keys;

                // migrate from localStorage
                if (keys.length === 0) {
                    for (let i = 0; i < window.localStorage.length; ++i) {
                        const key = window.localStorage.key(i);
                        const value = key && window.localStorage.getItem(key);
                        if (key != null && value !== null) {
                            await Preferences.set({ key, value });
                            keys.push(key);
                        }
                    }
                }

                for (const key of keys) {
                    if (key) {
                        if (!this.subjects.has(key)) {
                            this.subjects.set(key, new BehaviorSubject<any>(await this.capacitorLoadItem(key)));
                        }
                    }
                }
                this.readyPromiser.notify();
            });
        }
    }

    get ready() {
        return this.readyPromiser.get();
    }

    setItem = async (key: string, origValue: any): Promise<void> => {
        const value = cloneDeep(origValue);
        const subject = this.subjects.get(key);
        let save = false;
        if (subject) {
            if (!isEqual(subject.value, value)) {
                subject.next(value);
                save = true;
            }
        } else {
            this.subjects.set(key, new BehaviorSubject<any>(value));
            save = true;
        }

        if (save) {
            if (electronBridge) {
                this.electronSaveFile();
            } else {
                await Preferences.set({ key, value: JSON.stringify(value) });
            }
        }
    };
    getItem = async (key: string): Promise<Observable<any>> => {
        let subject = this.subjects.get(key);
        if (!subject) {
            let value: any;
            if (!electronBridge) {
                value = await this.capacitorLoadItem(key); // handle early loads
            }
            subject = new BehaviorSubject<any>(value);
            this.subjects.set(key, subject);
        }
        return subject.asObservable().pipe(map(cloneDeep));
    };
    readItem = async (key: string): Promise<any> => {
        const item = await this.getItem(key);
        return await firstValueFrom(item);
    };
    getEnumItem = async (key: string, values: string[]): Promise<Observable<string | undefined>> => {
        const obs = await this.getItem(key);
        return new Observable<string>((observer) => {
            const subscription = obs.subscribe({
                next: (value: any) => observer.next(values.includes(value) ? value : undefined),
                error: (err: any) => {
                    if (subscription) {
                        subscription.unsubscribe();
                    }
                    observer.error(err);
                },
                complete: () => {
                    if (subscription) {
                        subscription.unsubscribe();
                    }
                    observer.complete();
                },
            });
        });
    };
    readEnumItem = async (key: string, values: string[]): Promise<any> => {
        const item = await this.getEnumItem(key, values);
        return await firstValueFrom(item);
    };
    keys = async (): Promise<string[]> => Array.from(this.subjects.keys()).filter((key) => this.subjects.get(key)?.value !== undefined);
    remove = async (key: string): Promise<void> => {
        if (electronBridge) {
            await this.setItem(key, undefined);
        } else {
            await Preferences.remove({ key });
            const subject = this.subjects.get(key);
            if (subject) {
                subject.next(undefined);
            }
        }
    };
    clear = async (): Promise<void> => {
        if (electronBridge) {
            this.clearSubjects();
            this.electronSaveFile();
        } else {
            await Preferences.clear();
            this.clearSubjects();
        }
    };

    private clearSubjects = () => {
        for (const subj of this.subjects.values()) {
            subj.next(undefined);
        }
    };

    private capacitorLoadItem = async (key: string) => {
        const str = (await Preferences.get({ key })).value;
        if (str === undefined || str === null || str === 'undefined' || str === '') {
            return undefined;
        } else {
            try {
                return JSON.parse(str);
            } catch (err) {
                this.logger.warn(`JSON parse error loading ${key}, read`, str);
                return undefined;
            }
        }
    };

    private electronLoadFile = () => {
        let json: string;
        for (let attempts = 0; ; ) {
            try {
                json = fs.readFileSync(this.electronFilePath, { encoding: 'utf8' });
                break;
            } catch (err) {
                ++attempts;
                if (attempts >= 5) {
                    this.logger.error(err);
                    return;
                }
            }
        }
        let obj: Record<string, unknown>;
        try {
            obj = JSON.parse(json);
        } catch (err) {
            this.logger.error(err, 'while parsing', JSON.stringify(json));
            return;
        }

        for (const key of Object.keys(obj)) {
            const value = obj[key];
            const subject = this.subjects.get(key);
            if (subject) {
                if (!isEqual(subject.value, value)) {
                    subject.next(value);
                }
            } else {
                this.subjects.set(key, new BehaviorSubject<any>(value));
            }
        }
    };

    private electronSaveFile = () => {
        // Guard: only proceed if all required Node.js modules are available
        if (!fs || !path || !tmp) {
            return;
        }
        // dump the database
        const obj = {};
        for (const key of this.subjects.keys()) {
            const subject = this.subjects.get(key);
            if (subject?.value !== undefined) {
                obj[key] = subject.value;
            }
        }
        const json = JSON.stringify(obj, null, 4);
        for (let attempts = 0; ; ) {
            let temp: tmp_typeonly.FileResult | undefined;
            try {
                // make a temporary file
                temp = tmp.fileSync({
                    detachDescriptor: true,
                    dir: this.electronDirPath,
                    mode: 0o666,
                    tmpdir: this.electronDirPath,
                });
                fs.writeSync(temp.fd, json);
                fs.closeSync(temp.fd);
                // replace the original with the temporary
                fs.renameSync(temp.name, this.electronFilePath);
                break;
            } catch (err) {
                ++attempts;
                if (attempts >= 5) {
                    this.logger.error(err);
                    if (temp) {
                        try {
                            fs.unlinkSync(temp.name);
                        } catch {} // best-effort remove the file
                    }
                    return;
                }
            }
        }
    };
}
